import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AllState } from '../../../../store'
import {
  MilestoneDetail,
  MilestoneDeltaInput,
  MilestoneStatus,
} from '../../../../domain/entity/MilestoneEntity'
import { UserBasic } from '../../../../domain/value-object/UserBasicVO'
import { createDelta } from '../../../../domain/value-object/ItemDeltaInputVO'
import { formatDateTime } from '../../../../utils/date'
import { useMilestoneRepository } from '../../../../services/adaptors/milestoneRepositoryAdaptor'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import {
  ErrorCode,
  isPermissionError,
} from '../../../../handlers/globalErrorHandler'

export type Milestone = {
  uuid: string
  name: string
  milestoneDate: string
  description?: string
  iconName?: string
  color?: string
  achieved: boolean
  achievedAt?: string
  createdBy?: UserBasic
  createdAt?: string
  updatedBy?: UserBasic
  updatedAt?: string
  lockVersion: number
}

export type MilestoneWithState = Milestone & {
  originalData?: Milestone
}

export type SortOption =
  | 'date_asc'
  | 'date_desc'
  | 'achieved_first'
  | 'unachieved_first'
  | 'created_asc'
  | 'created_desc'

export type RightPanelMode = 'view' | 'edit'

export type ApiError = {
  code: string
  detail: string
}

export type FormState = {
  name: string
  milestoneDate: string
  description: string
  iconName: string
  color: string
  achieved: boolean
}

export const DEFAULT_ICON = 'mdi:rhombus'
export const DEFAULT_COLOR = '#000000'

export const PRESET_ICONS = [
  'mdi:rhombus',
  'mdi:star',
  'mdi:flag',
  'mdi:checkbox-marked',
  'mdi:package-variant-closed',
  'mdi:calendar-star',
]

export const PRESET_COLORS = [
  '#FF0000',
  '#ed6c02',
  '#ffc107',
  '#2e7d32',
  '#1976d2',
  '#9c27b0',
  '#000000',
]

const initialFormState: FormState = {
  name: '',
  milestoneDate: '',
  description: '',
  iconName: DEFAULT_ICON,
  color: DEFAULT_COLOR,
  achieved: false,
}

export const getMilestoneStatus = (milestone: Milestone): MilestoneStatus => {
  if (milestone.achieved) return 'achieved'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const milestoneDate = new Date(milestone.milestoneDate)
  if (milestoneDate < today) return 'overdue'
  return 'pending'
}

const toMilestoneDeltaInput = (
  m: MilestoneWithState
): MilestoneDeltaInput | null => {
  const original = m.originalData
  if (!original) return null

  const delta: MilestoneDeltaInput = {
    uuid: m.uuid,
    lockVersion: m.lockVersion,
  }

  const nameDelta = createDelta(original.name, m.name)
  if (nameDelta) delta.name = nameDelta
  const descDelta = createDelta(original.description, m.description)
  if (descDelta) delta.description = descDelta
  const dateDelta = createDelta(original.milestoneDate, m.milestoneDate)
  if (dateDelta) delta.milestoneDate = dateDelta
  const iconDelta = createDelta(original.iconName, m.iconName)
  if (iconDelta) delta.iconName = iconDelta
  const colorDelta = createDelta(original.color, m.color)
  if (colorDelta) delta.color = colorDelta
  const achievedDelta = createDelta(original.achieved, m.achieved)
  if (achievedDelta) delta.achieved = achievedDelta

  return delta
}

type Sortable = {
  milestoneDate: string
  achieved: boolean
  createdAt?: string | number
}

const sortByOption = <T extends Sortable>(
  items: T[],
  sortOption: SortOption
): T[] => {
  return [...items].sort((a, b) => {
    switch (sortOption) {
      case 'date_asc':
        return a.milestoneDate.localeCompare(b.milestoneDate)
      case 'date_desc':
        return b.milestoneDate.localeCompare(a.milestoneDate)
      case 'achieved_first':
        if (a.achieved === b.achieved) {
          return a.milestoneDate.localeCompare(b.milestoneDate)
        }
        return a.achieved ? -1 : 1
      case 'unachieved_first':
        if (a.achieved === b.achieved) {
          return a.milestoneDate.localeCompare(b.milestoneDate)
        }
        return a.achieved ? 1 : -1
      case 'created_asc':
        return String(a.createdAt ?? '').localeCompare(
          String(b.createdAt ?? '')
        )
      case 'created_desc':
        return String(b.createdAt ?? '').localeCompare(
          String(a.createdAt ?? '')
        )
      default:
        return 0
    }
  })
}

type UseMilestoneDialogProps = {
  initialMilestones?: MilestoneDetail[]
  initialSelectedUuid?: string
  onClose: () => void
  onSaveComplete?: () => void
}

export const useMilestoneDialog = ({
  initialMilestones,
  initialSelectedUuid,
  onClose,
  onSaveComplete,
}: UseMilestoneDialogProps) => {
  const projectUuid = useSelector((state: AllState) => state.project.selected)
  const dispatch = useDispatch()
  const milestoneRepository = useMilestoneRepository()

  const [milestones, setMilestones] = useState<MilestoneWithState[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('view')
  const [selectedMilestoneUuid, setSelectedMilestoneUuid] = useState<
    string | null
  >(null)
  const [sortOption, setSortOption] = useState<SortOption>('date_asc')
  const [formState, setFormState] = useState<FormState>(initialFormState)

  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false)
  const [deleteTargetUuid, setDeleteTargetUuid] = useState<string | null>(null)

  const [discardConfirmDialogOpen, setDiscardConfirmDialogOpen] =
    useState(false)
  const [pendingSelectUuid, setPendingSelectUuid] = useState<string | null>(
    null
  )

  const selectedMilestone = useMemo(
    () => milestones.find(m => m.uuid === selectedMilestoneUuid) ?? null,
    [milestones, selectedMilestoneUuid]
  )

  const hasFormChanges = useMemo(() => {
    if (rightPanelMode !== 'edit' || !selectedMilestone) return false
    const original = selectedMilestone.originalData ?? selectedMilestone
    return (
      formState.name !== original.name ||
      formState.milestoneDate !== original.milestoneDate ||
      formState.description !== (original.description ?? '') ||
      formState.iconName !== (original.iconName ?? DEFAULT_ICON) ||
      formState.color !== (original.color ?? DEFAULT_COLOR) ||
      formState.achieved !== original.achieved
    )
  }, [rightPanelMode, selectedMilestone, formState])

  const dateConflictMilestone = useMemo(() => {
    if (rightPanelMode !== 'edit' || !selectedMilestone) return null
    return (
      milestones.find(
        m =>
          m.uuid !== selectedMilestoneUuid &&
          m.milestoneDate === formState.milestoneDate
      ) ?? null
    )
  }, [
    rightPanelMode,
    selectedMilestone,
    selectedMilestoneUuid,
    milestones,
    formState.milestoneDate,
  ])

  const deleteTargetMilestone = useMemo(
    () => milestones.find(m => m.uuid === deleteTargetUuid) ?? null,
    [milestones, deleteTargetUuid]
  )

  const resetMilestones = useCallback(() => {
    if (initialMilestones) {
      setMilestones(
        initialMilestones.map(detail => ({
          uuid: detail.uuid,
          name: detail.name,
          milestoneDate: detail.milestoneDate,
          description: detail.description,
          iconName: detail.iconName,
          color: detail.color,
          achieved: detail.achieved,
          achievedAt: detail.achievedAt
            ? formatDateTime(detail.achievedAt)
            : undefined,
          createdBy: detail.createdBy,
          createdAt: formatDateTime(detail.createdAt),
          updatedBy: detail.updatedBy,
          updatedAt: detail.updatedAt
            ? formatDateTime(detail.updatedAt)
            : undefined,
          lockVersion: detail.lockVersion,
        }))
      )
    }
  }, [initialMilestones])

  useEffect(() => {
    resetMilestones()
  }, [resetMilestones])

  const handleDialogEntered = useCallback(() => {
    resetMilestones()

    if (initialSelectedUuid) {
      const milestone = initialMilestones?.find(
        milestone => milestone.uuid === initialSelectedUuid
      )
      if (milestone) {
        setSelectedMilestoneUuid(initialSelectedUuid)
        setRightPanelMode('edit')
        setFormState({
          name: milestone.name,
          milestoneDate: milestone.milestoneDate,
          description: milestone.description ?? '',
          iconName: milestone.iconName ?? DEFAULT_ICON,
          color: milestone.color ?? DEFAULT_COLOR,
          achieved: milestone.achieved,
        })
        return
      }
    }

    if (initialMilestones && initialMilestones.length > 0) {
      const sorted = sortByOption(initialMilestones, sortOption)
      setSelectedMilestoneUuid(sorted[0].uuid)
      setRightPanelMode('view')
    } else {
      setSelectedMilestoneUuid(null)
      setRightPanelMode('view')
    }
  }, [resetMilestones, initialSelectedUuid, initialMilestones, sortOption])

  const handleCloseDialog = useCallback(() => {
    if (hasFormChanges) {
      setDiscardConfirmDialogOpen(true)
      setPendingSelectUuid(null)
      return
    }
    onClose()
  }, [hasFormChanges, onClose])

  const handleSelectMilestone = useCallback(
    (uuid: string) => {
      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone) return

      if (hasFormChanges) {
        setDiscardConfirmDialogOpen(true)
        setPendingSelectUuid(uuid)
        return
      }

      setSelectedMilestoneUuid(uuid)
      setRightPanelMode('view')
    },
    [milestones, hasFormChanges]
  )

  const handleStartEdit = useCallback(
    (uuid: string) => {
      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone) return
      setSelectedMilestoneUuid(uuid)
      setRightPanelMode('edit')
      setFormState({
        name: milestone.name,
        milestoneDate: milestone.milestoneDate,
        description: milestone.description ?? '',
        iconName: milestone.iconName ?? DEFAULT_ICON,
        color: milestone.color ?? DEFAULT_COLOR,
        achieved: milestone.achieved,
      })
    },
    [milestones]
  )

  const updateMilestoneField = useCallback(
    (uuid: string, updates: Partial<Milestone>) => {
      setMilestones(prev =>
        prev.map(m => {
          if (m.uuid !== uuid) return m
          return {
            ...m,
            ...updates,
            originalData: m.originalData ?? { ...m },
          }
        })
      )
    },
    []
  )

  const handleUpdateField = useCallback(
    (field: keyof FormState, value: string | boolean) => {
      if (!selectedMilestoneUuid) return
      updateMilestoneField(selectedMilestoneUuid, { [field]: value })
      setFormState(prev => ({ ...prev, [field]: value }))
    },
    [selectedMilestoneUuid, updateMilestoneField]
  )

  const handleRequestDelete = useCallback((uuid: string) => {
    setDeleteTargetUuid(uuid)
    setDeleteConfirmDialogOpen(true)
  }, [])

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmDialogOpen(false)
    setDeleteTargetUuid(null)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!projectUuid || !deleteTargetUuid) return

    const milestone = milestones.find(m => m.uuid === deleteTargetUuid)
    if (!milestone) return

    setIsSaving(true)
    try {
      await milestoneRepository.updateBatchDelta({
        added: [],
        edited: [],
        deleted: [
          { uuid: deleteTargetUuid, lockVersion: milestone.lockVersion },
        ],
      })

      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'milestone.message.deleteSuccess' }),
        })
      )

      setMilestones(prev => {
        const updated = prev.filter(m => m.uuid !== deleteTargetUuid)

        if (updated.length > 0) {
          const sorted = sortByOption(updated, sortOption)
          setSelectedMilestoneUuid(sorted[0].uuid)
        } else {
          setSelectedMilestoneUuid(null)
        }
        setRightPanelMode('view')

        return updated
      })

      onSaveComplete?.()
    } catch (error) {
      console.error('Failed to delete milestone:', error)
      if (isPermissionError(error)) {
        throw error
      }
      const apiError = error as ApiError
      const isBusinessError = apiError?.code === ErrorCode.BUSINESS_ERROR
      dispatch(
        addGlobalMessage({
          type: isBusinessError ? MessageLevel.WARN : MessageLevel.ERROR,
          title: isBusinessError
            ? apiError.detail
            : intl.formatMessage({ id: 'milestone.error.deleteFailed' }),
        })
      )
    } finally {
      setIsSaving(false)
      setDeleteConfirmDialogOpen(false)
      setDeleteTargetUuid(null)
    }
  }, [
    projectUuid,
    deleteTargetUuid,
    milestones,
    milestoneRepository,
    sortOption,
    dispatch,
    onSaveComplete,
  ])

  const handleCancelDiscard = useCallback(() => {
    setDiscardConfirmDialogOpen(false)
    setPendingSelectUuid(null)
  }, [])

  const handleConfirmDiscard = useCallback(() => {
    setDiscardConfirmDialogOpen(false)

    if (pendingSelectUuid === null) {
      onClose()
    } else {
      setMilestones(prev =>
        prev.map(m => {
          if (m.uuid !== selectedMilestoneUuid) return m
          if (m.originalData) {
            return {
              ...m.originalData,
              originalData: undefined,
            }
          }
          return m
        })
      )
      setSelectedMilestoneUuid(pendingSelectUuid)
      setRightPanelMode('view')
      setPendingSelectUuid(null)
    }
  }, [pendingSelectUuid, selectedMilestoneUuid, onClose])

  const handleToggleAchieved = useCallback(
    async (uuid: string) => {
      if (!projectUuid || isSaving) return

      const milestone = milestones.find(m => m.uuid === uuid)
      if (!milestone) return

      const newAchieved = !milestone.achieved
      const newAchievedAt = newAchieved ? formatDateTime(Date.now()) : undefined

      setIsSaving(true)
      try {
        const delta: MilestoneDeltaInput = {
          uuid: milestone.uuid,
          lockVersion: milestone.lockVersion,
          achieved: createDelta(milestone.achieved, newAchieved),
        }

        await milestoneRepository.updateBatchDelta({
          added: [],
          edited: [delta],
          deleted: [],
        })

        setMilestones(prev =>
          prev.map(m => {
            if (m.uuid !== uuid) return m
            return {
              ...m,
              achieved: newAchieved,
              achievedAt: newAchievedAt,
            }
          })
        )

        dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: newAchieved
                ? 'milestone.message.achievedSuccess'
                : 'milestone.message.unachievedSuccess',
            }),
          })
        )

        onSaveComplete?.()
      } catch (error) {
        console.error('Failed to toggle achieved:', error)
        if (isPermissionError(error)) {
          throw error
        }
        const apiError = error as ApiError
        const isBusinessError = apiError?.code === ErrorCode.BUSINESS_ERROR
        dispatch(
          addGlobalMessage({
            type: isBusinessError ? MessageLevel.WARN : MessageLevel.ERROR,
            title: isBusinessError
              ? apiError.detail
              : intl.formatMessage({ id: 'milestone.error.editFailed' }),
          })
        )
      } finally {
        setIsSaving(false)
      }
    },
    [
      projectUuid,
      milestones,
      isSaving,
      milestoneRepository,
      dispatch,
      onSaveComplete,
    ]
  )

  const handleCancelEdit = useCallback(() => {
    if (!selectedMilestone) return

    setMilestones(prev =>
      prev.map(m => {
        if (m.uuid !== selectedMilestoneUuid) return m
        if (m.originalData) {
          return {
            ...m.originalData,
            originalData: undefined,
          }
        }
        return m
      })
    )
    setRightPanelMode('view')
  }, [selectedMilestone, selectedMilestoneUuid])

  const handleSaveEdit = useCallback(async () => {
    if (!projectUuid || !selectedMilestone) return

    const delta = toMilestoneDeltaInput(selectedMilestone)
    if (!delta) {
      setRightPanelMode('view')
      return
    }

    setIsSaving(true)
    try {
      await milestoneRepository.updateBatchDelta({
        added: [],
        edited: [delta],
        deleted: [],
      })

      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'milestone.message.editSuccess' }),
        })
      )

      setMilestones(prev =>
        prev.map(m => {
          if (m.uuid !== selectedMilestoneUuid) return m
          return {
            ...m,
            originalData: undefined,
          }
        })
      )
      setRightPanelMode('view')
      onSaveComplete?.()
    } catch (error) {
      console.error('Failed to save milestone:', error)
      if (isPermissionError(error)) {
        throw error
      }
      const apiError = error as ApiError
      const isBusinessError = apiError?.code === ErrorCode.BUSINESS_ERROR
      dispatch(
        addGlobalMessage({
          type: isBusinessError ? MessageLevel.WARN : MessageLevel.ERROR,
          title: isBusinessError
            ? apiError.detail
            : intl.formatMessage({ id: 'milestone.error.editFailed' }),
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [
    projectUuid,
    selectedMilestone,
    selectedMilestoneUuid,
    milestoneRepository,
    dispatch,
    onSaveComplete,
  ])

  const displayMilestones = useMemo(() => {
    return sortByOption(milestones, sortOption)
  }, [milestones, sortOption])

  return {
    displayMilestones,
    selectedMilestone,
    isSaving,
    rightPanelMode,
    sortOption,
    setSortOption,
    formState,
    hasFormChanges,
    dateConflictMilestone,
    deleteConfirmDialogOpen,
    deleteTargetMilestone,
    discardConfirmDialogOpen,
    handleDialogEntered,
    handleCloseDialog,
    handleSelectMilestone,
    handleStartEdit,
    handleUpdateField,
    handleToggleAchieved,
    handleCancelEdit,
    handleSaveEdit,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
    handleCancelDiscard,
    handleConfirmDiscard,
  }
}
